import { useState, useCallback, type FC, type ComponentType, type CSSProperties } from 'react';
import SmartSelectRaw from '@/components/ui/SmartSelect';
import SwitchRaw from '@/components/ui/Switch';
import { DRAWER_COMPONENTS } from '@/components/ui/GenericDrawer';

// === File-local companions ============================================

type BulkEditFieldType = 'text' | 'select' | 'async-select' | 'boolean' | 'number' | 'textarea';

interface BulkEditOption {
  value: string | number;
  label: string;
}

type BulkEditPrimitiveValue = string | number | boolean | null;
type BulkEditAsyncValue = { value: unknown; label: string } | null;
type BulkEditValue = BulkEditPrimitiveValue | BulkEditAsyncValue;

type BulkEditOnChange = (fieldName: string, value: BulkEditValue) => void;
type BulkEditOnToggle = (fieldName: string) => void;
type BulkEditAsyncSearch = (query: string) => Promise<BulkEditOption[]>;

interface BulkEditFieldProps {
  name: string;
  label: string;
  type?: BulkEditFieldType;
  value?: BulkEditValue;
  onChange: BulkEditOnChange;
  selected?: boolean;
  onToggle: BulkEditOnToggle;
  options?: BulkEditOption[];
  onSearch?: BulkEditAsyncSearch | null;
  placeholder?: string;
  disabled?: boolean;
}

interface BulkEditFieldConfig {
  name: string;
  label: string;
  type?: BulkEditFieldType;
  options?: BulkEditOption[];
  placeholder?: string;
  onSearch?: BulkEditAsyncSearch | null;
  lookup?: string;
  labelFn?: (item: Record<string, unknown>) => string;
}

type BulkEditLookupItem = Record<string, unknown> & { id: string | number };

interface BulkEditFormProps {
  fields?: BulkEditFieldConfig[];
  values?: Record<string, BulkEditValue>;
  onChange: BulkEditOnChange;
  selectedFields?: Record<string, boolean>;
  onToggleField: BulkEditOnToggle;
  lookups?: Record<string, BulkEditLookupItem[]>;
  asyncSearchFunctions?: Record<string, BulkEditAsyncSearch>;
}

interface BulkEditHookResult {
  selectedFields: Record<string, boolean>;
  toggleField: (fieldName: string) => void;
  resetFields: () => void;
  // formData is loosened to Record<string, unknown> so hub (GenericDrawer) can
  // pass its own GenericDrawerRow shape without an extra cast at the call site.
  getSelectedData: (formData: Record<string, unknown>) => Record<string, unknown>;
  hasSelectedFields: () => boolean;
}

// Raw-alias untyped sibling components so local Props typing governs call sites.
const SmartSelect = SmartSelectRaw as unknown as ComponentType<Record<string, unknown>>;
const Switch = SwitchRaw as unknown as ComponentType<Record<string, unknown>>;

// Narrow DRAWER_COMPONENTS input style to CSSProperties at the usage boundary.
// Lazy getter — DRAWER_COMPONENTS is imported from GenericDrawer which transitively
// imports BulkEditField, so module-top-level access triggers a temporal-dead-zone
// runtime error. Calling at render time is safe because the cycle has resolved by then.
const getDrawerInputStyle = (): CSSProperties =>
  (DRAWER_COMPONENTS as unknown as { input: CSSProperties }).input;

// Coerce a BulkEditValue to a primitive scalar acceptable by native HTML inputs.
// For async-select object values this returns the label; other non-primitives fall back to ''.
const toInputScalar = (v: BulkEditValue | undefined): string | number => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string' || typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 'true' : '';
  // async-select object { value, label }
  if (typeof v === 'object' && 'label' in v && typeof v.label === 'string') return v.label;
  return '';
};

/**
 * BulkEditField - Componente para campos editáveis em modo bulk edit
 *
 * Renderiza um campo com checkbox que controla se o campo será incluído na edição em lote.
 * O campo fica desabilitado até o checkbox ser marcado.
 *
 * @param {string} name - Nome do campo (ex: 'ponto_tipo_id')
 * @param {string} label - Label do campo (ex: 'Tipo de Ponto')
 * @param {'text'|'select'|'async-select'|'boolean'} type - Tipo do campo
 * @param {any} value - Valor atual do campo (para async-select: { value, label } ou null)
 * @param {function} onChange - Callback quando valor muda: (fieldName, value) => void
 * @param {boolean} selected - Se o campo está selecionado para edição
 * @param {function} onToggle - Callback quando checkbox é marcado/desmarcado: (fieldName) => void
 * @param {Array} options - Opções para campos select (ex: [{ value: 1, label: 'Opção 1' }])
 * @param {function} onSearch - Função de busca async para campos async-select: (query) => Promise<Array>
 * @param {string} placeholder - Placeholder para campos text/select
 * @param {boolean} disabled - Desabilita o campo completamente
 *
 * @example
 * <BulkEditField
 *   name="ponto_tipo_id"
 *   label="Tipo de Ponto"
 *   type="select"
 *   value={formData.ponto_tipo_id}
 *   onChange={handleFieldChange}
 *   selected={selectedFields.ponto_tipo_id}
 *   onToggle={handleToggle}
 *   options={pontoTipos.map(t => ({ value: t.id, label: t.ponto_tipo }))}
 *   placeholder="Selecione o tipo..."
 * />
 */
const BulkEditField: FC<BulkEditFieldProps> = ({
  name,
  label,
  type = 'text',
  value,
  onChange,
  selected = false,
  onToggle,
  options = [],
  onSearch = null,
  placeholder = '',
  disabled = false
}) => {
  const isFieldEnabled = selected && !disabled;

  const handleToggle = useCallback(() => {
    if (!disabled) {
      onToggle(name);
    }
  }, [name, onToggle, disabled]);

  const handleChange = useCallback((newValue: BulkEditValue) => {
    onChange(name, newValue);
  }, [name, onChange]);

  // Estilos do container baseado no estado
  const containerStyle: CSSProperties = {
    padding: '12px',
    border: `1px solid ${selected ? 'var(--primary)' : 'var(--panel-border)'}`,
    borderRadius: '8px',
    backgroundColor: selected ? 'var(--color-primary-light)' : 'transparent',
    transition: 'all 0.15s ease',
    opacity: disabled ? 0.6 : 1
  };

  // Renderiza o input baseado no tipo
  const renderInput = () => {
    switch (type) {
      case 'boolean':
        return (
          <Switch
            checked={value || false}
            onChange={handleChange}
            disabled={!isFieldEnabled}
          />
        );

      case 'select':
        return (
          <SmartSelect
            options={options}
            value={value}
            onChange={(opt: BulkEditOption | null) => handleChange(opt?.value || null)}
            label={label.toLowerCase()}
            isDisabled={!isFieldEnabled}
          />
        );

      case 'async-select':
        return (
          <SmartSelect
            onSearch={onSearch}
            value={value}
            onChange={(opt: BulkEditAsyncValue) => handleChange(opt)}
            label={label.toLowerCase()}
            isDisabled={!isFieldEnabled}
            debounceMs={300}
            minChars={0}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={toInputScalar(value)}
            onChange={(e) => handleChange(e.target.value ? Number(e.target.value) : null)}
            style={{
              ...getDrawerInputStyle(),
              opacity: isFieldEnabled ? 1 : 0.5,
              cursor: isFieldEnabled ? 'text' : 'not-allowed'
            }}
            placeholder={placeholder}
            disabled={!isFieldEnabled}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={toInputScalar(value)}
            onChange={(e) => handleChange(e.target.value)}
            style={{
              ...getDrawerInputStyle(),
              minHeight: '60px',
              resize: 'vertical',
              opacity: isFieldEnabled ? 1 : 0.5,
              cursor: isFieldEnabled ? 'text' : 'not-allowed'
            }}
            placeholder={placeholder}
            disabled={!isFieldEnabled}
          />
        );

      case 'text':
      default:
        return (
          <input
            type="text"
            value={toInputScalar(value)}
            onChange={(e) => handleChange(e.target.value)}
            style={{
              ...getDrawerInputStyle(),
              opacity: isFieldEnabled ? 1 : 0.5,
              cursor: isFieldEnabled ? 'text' : 'not-allowed'
            }}
            placeholder={placeholder}
            disabled={!isFieldEnabled}
          />
        );
    }
  };

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
            minWidth: '120px',
            flexShrink: 0
          }}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={handleToggle}
            disabled={disabled}
            style={{
              width: '16px',
              height: '16px',
              accentColor: 'var(--primary)',
              cursor: disabled ? 'not-allowed' : 'pointer'
            }}
          />
          <span className="bulk-edit-label" style={{ fontSize: '13px', fontWeight: 500, color: '#6b7280' }}>
            {label}
          </span>
        </label>
        <div style={{ flex: 1, minWidth: 0 }}>
          {renderInput()}
        </div>
      </div>
    </div>
  );
};

export default BulkEditField;

/**
 * BulkEditForm - Container para múltiplos BulkEditFields
 *
 * Gerencia o estado de campos selecionados e facilita a integração com drawers.
 *
 * @param {Array} fields - Configuração dos campos: [{ name, label, type, options, placeholder, onSearch }]
 * @param {Object} values - Valores dos campos: { fieldName: value }
 * @param {function} onChange - Callback quando valor muda: (fieldName, value) => void
 * @param {Object} selectedFields - Campos selecionados: { fieldName: boolean }
 * @param {function} onToggleField - Callback quando checkbox é marcado: (fieldName) => void
 * @param {Object} lookups - Dados de lookup para campos select: { lookupName: [items] }
 * @param {Object} asyncSearchFunctions - Funções de busca para campos async-select: { fieldName: (query) => Promise }
 *
 * @example
 * <BulkEditForm
 *   fields={[
 *     { name: 'previsao', label: 'Previsão', type: 'boolean' },
 *     { name: 'ponto_tipo_id', label: 'Tipo de Ponto', type: 'select', lookup: 'ponto_tipo' }
 *   ]}
 *   values={formData}
 *   onChange={handleFieldChange}
 *   selectedFields={selectedBulkFields}
 *   onToggleField={handleBulkFieldToggle}
 *   lookups={{ ponto_tipo: pontoTipos }}
 * />
 */
export const BulkEditForm: FC<BulkEditFormProps> = ({
  fields = [],
  values = {},
  onChange,
  selectedFields = {},
  onToggleField,
  lookups = {},
  asyncSearchFunctions = {}
}) => {
  // Resolver opções para campos select baseado em lookup
  const getFieldOptions = (field: BulkEditFieldConfig): BulkEditOption[] => {
    if (field.options) return field.options;
    if (field.lookup && lookups[field.lookup]) {
      const lookupData = lookups[field.lookup];
      return lookupData.map((item) => {
        // labelFn customizado tem prioridade; fallback percorre chaves comuns.
        const derivedLabel = field.labelFn
          ? field.labelFn(item)
          : ((item.nome as string | undefined) ||
             (item.name as string | undefined) ||
             (item.label as string | undefined) ||
             (item.descricao as string | undefined) ||
             (field.lookup ? (item[field.lookup] as string | undefined) : undefined) ||
             Object.values(item).find((v) => typeof v === 'string' && v !== item.id) as string | undefined ||
             String(item.id));
        return {
          value: item.id,
          label: derivedLabel
        };
      });
    }
    return [];
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
        Marque os campos que deseja alterar e preencha com o novo valor.
      </p>

      {fields.map((field) => {
        // Determinar tipo: se tem lookup, é select; se tem onSearch, é async-select
        const fieldType: BulkEditFieldType = field.type || (field.onSearch ? 'async-select' : field.lookup ? 'select' : 'text');
        // Função de busca async (pode vir do field ou do asyncSearchFunctions)
        const searchFn: BulkEditAsyncSearch | null = field.onSearch || asyncSearchFunctions[field.name] || null;

        return (
          <BulkEditField
            key={field.name}
            name={field.name}
            label={field.label}
            type={fieldType}
            value={values[field.name]}
            onChange={onChange}
            selected={selectedFields[field.name] || false}
            onToggle={onToggleField}
            options={getFieldOptions(field)}
            onSearch={searchFn}
            placeholder={field.placeholder || `Selecione ${field.label.toLowerCase()}...`}
          />
        );
      })}
    </div>
  );
};

/**
 * useBulkEdit - Hook para gerenciar estado de edição em lote
 *
 * @returns {Object} { selectedFields, toggleField, resetFields, getSelectedData }
 *
 * @example
 * const { selectedFields, toggleField, resetFields, getSelectedData } = useBulkEdit();
 *
 * // No handleSave:
 * const fieldsToUpdate = getSelectedData(formData);
 * if (Object.keys(fieldsToUpdate).length === 0) {
 *   showError('Selecione pelo menos um campo');
 *   return;
 * }
 * onSave(fieldsToUpdate);
 */
export function useBulkEdit(): BulkEditHookResult {
  const [selectedFields, setSelectedFields] = useState<Record<string, boolean>>({});

  const toggleField = useCallback((fieldName: string) => {
    setSelectedFields(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  }, []);

  const resetFields = useCallback(() => {
    setSelectedFields({});
  }, []);

  const getSelectedData = useCallback((formData: Record<string, unknown>): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    Object.keys(selectedFields).forEach(fieldName => {
      if (selectedFields[fieldName]) {
        // ?? null: campo checado mas não preenchido deve enviar null (limpar),
        // não undefined (que seria interpretado como "não alterar")
        result[fieldName] = formData[fieldName] ?? null;
      }
    });
    return result;
  }, [selectedFields]);

  const hasSelectedFields = useCallback(() => {
    return Object.values(selectedFields).some(v => v === true);
  }, [selectedFields]);

  return {
    selectedFields,
    toggleField,
    resetFields,
    getSelectedData,
    hasSelectedFields
  };
}
